function getConfig(name, defaultValue = null) {
  // If inside a docker container, use window.ENV
  if (typeof window !== "undefined" && window.ENV !== undefined) {
    return window.ENV[name] || defaultValue;
  }

  return import.meta.env[name] || defaultValue;
}

function parseBooleanConfig(name, defaultValue) {
  const raw = getConfig(name);
  if (raw === null || raw === undefined || raw === "") {
    return defaultValue;
  }

  const normalized = String(raw).trim().toLowerCase();
  if (["1", "true", "yes", "on", "enabled"].includes(normalized)) {
    return true;
  }
  if (["0", "false", "no", "off", "disabled"].includes(normalized)) {
    return false;
  }
  return defaultValue;
}

const DEFAULT_PUBLIC_SITE_ORIGIN = "https://maisvelozimoveis.com.br";

const normalizeSiteCandidate = value => {
  if (!value || !String(value).trim()) {
    return null;
  }

  const normalizedValue = String(value).trim();
  if (/^https?:\/\//i.test(normalizedValue)) {
    return normalizedValue;
  }

  return `https://${normalizedValue.replace(/^\/+/, "")}`;
};

const normalizeHostname = value =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^www\./, "")
    .replace(/\.$/, "");

function getConfiguredPublicSiteCandidate() {
  return normalizeSiteCandidate(
    getConfig("VITE_PUBLIC_SITE_URL") || DEFAULT_PUBLIC_SITE_ORIGIN
  );
}

function inferLocalBackendUrl() {
  if (typeof window === "undefined") {
    return "http://localhost:8080";
  }

  const { protocol, hostname, port } = window.location;
  const explicitPort = getConfig("VITE_BACKEND_PORT");

  if (explicitPort) {
    const sanitizedPort = String(explicitPort).replace(/^:/, "");
    return `${protocol}//${hostname}:${sanitizedPort}`;
  }

  const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";

  if (isLocalhost) {
    // Prefer a running backend on the current port (e.g. Docker setups) and fall back to 8080.
    if (port && port !== "" && port !== "3000") {
      return `${protocol}//${hostname}:${port}`;
    }

    return `${protocol}//${hostname}:8080`;
  }

  return `${protocol}//${hostname}${port ? `:${port}` : ""}`;
}

function inferDefaultBackendUrl() {
  if (typeof window === "undefined") {
    return "https://rapidsoft.app";
  }

  const { hostname } = window.location;
  const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
  const isPrimaryAppHost = hostname === "rapidsoft.app" || hostname === "www.rapidsoft.app";

  if (isLocalhost || isPrimaryAppHost) {
    return inferLocalBackendUrl();
  }

  return "https://rapidsoft.app";
}

export function getBackendUrl() {
  const configuredUrl = getConfig("VITE_BACKEND_URL");

  if (configuredUrl && String(configuredUrl).trim().length > 0) {
    return String(configuredUrl).trim();
  }

  return inferDefaultBackendUrl();
}

export function getGoogleMapsApiKey() {
  return (
    getConfig("VITE_GOOGLE_MAPS_API_KEY") ||
    getConfig("GOOGLE_MAPS_BROWSER_API_KEY") ||
    null
  );
}

export function getHoursCloseTicketsAuto() {
  return getConfig("VITE_HOURS_CLOSE_TICKETS_AUTO");
}

export function isCompactViewsEnabled() {
  return parseBooleanConfig("VITE_FF_API_COMPACT_VIEWS", true);
}

export function isBuyerInterestsDistributionProjectionEnabled() {
  return parseBooleanConfig("VITE_FF_BUYER_INTERESTS_DISTRIBUTION_PROJECTION", true);
}

export function isTeamDistributionMetricsOnDemandEnabled() {
  return parseBooleanConfig("VITE_FF_TEAM_DISTRIBUTION_METRICS_ON_DEMAND", true);
}

export function isDistributionBootstrapEndpointEnabled() {
  return parseBooleanConfig("VITE_FF_DISTRIBUTION_BOOTSTRAP_ENDPOINT", false);
}

export function isDashboardOverviewEndpointEnabled() {
  return parseBooleanConfig("VITE_FF_API_DASHBOARD_OVERVIEW", false);
}

function resolvePublicSiteCandidate() {
  const configured = getConfiguredPublicSiteCandidate();
  if (configured) {
    return configured;
  }

  if (typeof window !== "undefined" && window.location?.origin && isPublicSiteHost(window.location.hostname)) {
    return window.location.origin;
  }

  return DEFAULT_PUBLIC_SITE_ORIGIN;
}

export function getPublicSiteOrigin() {
  try {
    return new URL(resolvePublicSiteCandidate()).origin;
  } catch {
    return DEFAULT_PUBLIC_SITE_ORIGIN;
  }
}

export function isPublicSiteHost(hostname) {
  const currentHostname = normalizeHostname(
    hostname || (typeof window !== "undefined" ? window.location?.hostname : "")
  );
  if (!currentHostname) {
    return false;
  }

  if (
    currentHostname === "rapidsoft.app" ||
    (currentHostname.endsWith(".rapidsoft.app") &&
      !currentHostname.startsWith("maisvelozimoveis.com.br"))
  ) {
    return false;
  }

  try {
    const configuredOrigin = getConfiguredPublicSiteCandidate() || DEFAULT_PUBLIC_SITE_ORIGIN;
    const configuredHostname = normalizeHostname(new URL(configuredOrigin).hostname);
    return (
      currentHostname === configuredHostname ||
      currentHostname === `${configuredHostname}.rapidsoft.app`
    );
  } catch {
    const defaultHostname = normalizeHostname(new URL(DEFAULT_PUBLIC_SITE_ORIGIN).hostname);
    return (
      currentHostname === defaultHostname ||
      currentHostname === `${defaultHostname}.rapidsoft.app`
    );
  }
}
